import { Instagram, Phone, Mail, MapPin, Facebook, Twitter } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/tommy-hour-logo.jpg";

export function Footer() {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: "Menu", href: "/#menu" },
    { label: "Events", href: "/#events" },
    { label: "Gallery", href: "/#gallery" },
    { label: "Reservation", href: "/#reservation" },
  ];

  const companyLinks = [
    { label: "About Us", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "FAQ", href: "/faq" },
    { label: "Accessibility", href: "/accessibility" },
  ];

  const legalLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Cookie Policy", href: "/cookie-policy" },
    { label: "Accessibility", href: "/accessibility" },
  ];

  return (
    <footer className="bg-card/50 border-t border-border/30 py-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 md:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={logo}
                alt="Tommy Hour Logo"
                className="h-14 w-14 rounded-full object-cover ring-2 ring-primary/20"
              />
              <div>
                <h3 className="font-display text-2xl text-foreground">TOMMY HOUR</h3>
                <p className="text-sm text-muted-foreground">Bar & Restaurant</p>
              </div>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mb-4">
              Bangkok's premier nightlife destination. Premium cocktails, 
              unforgettable nights, and moments that matter.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com/tommyhour"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-card/80 border border-border/30 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com/tommyhour"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-card/80 border border-border/30 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com/tommyhour"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-card/80 border border-border/30 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Company</h4>
            <ul className="space-y-2">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>Sukhumvit Soi 11, Bangkok</span>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-primary" />
                <a href="tel:+66212345678" className="hover:text-primary transition-colors">
                  +66 2 123 4567
                </a>
              </li>
              <li className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4 text-primary" />
                <a href="mailto:hello@tommyhour.com" className="hover:text-primary transition-colors">
                  hello@tommyhour.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Links */}
        <div className="border-t border-border/30 mt-12 pt-8">
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {legalLinks.map((link, index) => (
              <span key={link.label} className="flex items-center gap-4">
                <Link
                  to={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  {link.label}
                </Link>
                {index < legalLinks.length - 1 && (
                  <span className="text-border">•</span>
                )}
              </span>
            ))}
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-sm">
              © {currentYear} Tommy Hour. All rights reserved.
            </p>
            <p className="text-muted-foreground text-xs">
              Please drink responsibly. 21+ only.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
